'use client';

import { useCallback } from 'react';

import type { ChatMessageAPI } from '../../hooks/useChatMessageAPI';
import { ChatMessageType } from '../../types/ChatMessage';
import type { ChatSessionType } from '../../types/ChatSessionType';
import type { ChatUser } from '../../types/ChatUser';
import {
  readImageDimensions,
  readVideoDimensions,
} from './functions/readMediaDimensions';

interface Params {
  sendMessage: ChatMessageAPI['sendMessage'];
  sessionType: ChatSessionType;
  currentUser: ChatUser;
  targetUser: ChatUser;
}

export interface ShareMedia {
  sharePhoto: (file: File) => void;
  shareVideo: (file: File) => void;
}

/**
 * "Share a Photo / Video" handlers. Reads the file's intrinsic size,
 * builds an optimistic LOCAL content (blob preview + the held `File`)
 * and hands it to `sendMessage` — the upload to Firebase Storage then
 * happens inside the send pipeline (`prepareMessageContent`), so the
 * bubble paints instantly and swaps to the remote URL on the gateway
 * echo. 1:1 with PopshopRN's `shareMediaFile`.
 */
export function useShareMedia({
  sendMessage,
  sessionType,
  currentUser,
  targetUser,
}: Params): ShareMedia {
  const sharePhoto = useCallback(
    (file: File) => {
      void (async () => {
        try {
          const { width, height } = await readImageDimensions(file);
          await sendMessage({
            sessionType,
            type: ChatMessageType.Picture,
            from: currentUser,
            to: targetUser,
            payload: {
              localFileUri: URL.createObjectURL(file),
              localFile: file,
              width,
              height,
            },
            quoted: undefined,
          });
        } catch (error) {
          console.error('share photo failed:', error);
        }
      })();
    },
    [sendMessage, sessionType, currentUser, targetUser],
  );

  const shareVideo = useCallback(
    (file: File) => {
      void (async () => {
        try {
          const { width, height } = await readVideoDimensions(file);
          await sendMessage({
            sessionType,
            type: ChatMessageType.Video,
            from: currentUser,
            to: targetUser,
            payload: {
              localFileUri: URL.createObjectURL(file),
              localFile: file,
              width,
              height,
            },
            quoted: undefined,
          });
        } catch (error) {
          console.error('share video failed:', error);
        }
      })();
    },
    [sendMessage, sessionType, currentUser, targetUser],
  );

  return { sharePhoto, shareVideo };
}
